import React, { useEffect, useState } from "react";
import axios from "axios";
import CustomNavbar from "./CustomPermissions/CustomNavbar";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../config/api";

const FilterMonthlyRecords = () => {
  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track expanded row with a composite key (userIndex-rowIndex)
  const [expandedKey, setExpandedKey] = useState(null);
  const [expandedRowType, setExpandedRowType] = useState(null);

  const [candidateDetails, setCandidateDetails] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const response = await axios.get(getApiUrl("/FilterMonthlyRecords/"), {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        setRecords(response.data);
      } catch (err) {
        setError("Failed to fetch records");
      } finally {
        setLoading(false);
      }
    };
    fetchRecords();
  }, []);

  const sortedRecords = Object.entries(records).sort(([a], [b]) => a.localeCompare(b));

  // Filter records based on search term
  const filteredRecords = sortedRecords.filter(([username, userData]) =>
    userData.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleExpandedRow = async (userIndex, rowIndex, type, monthStart, monthEnd, userId) => {
    const newKey = `${userIndex}-${rowIndex}`;

    if (expandedKey === newKey && expandedRowType === type) {
      // Collapse the row if already expanded
      setExpandedKey(null);
      setExpandedRowType(null);
      setCandidateDetails([]);
    } else {
      // Expand the row and fetch candidate details
      setExpandedKey(newKey);
      setExpandedRowType(type);
      setLoadingCandidates(true);

      try {
        const response = await axios.get(
          getApiUrl(`/GetAllCandidateOfUser/${userId}/`),
          {
            params: {
              start_date: monthStart,
              end_date: monthEnd,
              status: type,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        setCandidateDetails(response.data.data || []);
      } catch (error) {
        console.error("Error fetching candidate details:", error);
      } finally {
        setLoadingCandidates(false);
      }
    }
  };

  if (loading) return <div className="text-center mt-5">Loading records...</div>;
  if (error) return <div className="text-center text-red-500 mt-5">{error}</div>;

  return (
    <>
      <div className="flex">
        <CustomNavbar />
        <div style={{ width: "100vw" }}>
          <h1 className="text-2xl font-bold bg-blue-500 text-white p-4">
            HR Associate&apos;s Month-wise Record
          </h1>
          {/* Search Bar */}
          <div style={{ width: "90vw", margin: "20px auto" }}>
            <div className="bg-white p-4 rounded-lg shadow-md">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by HR Associate name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <svg
                  className="absolute left-3 top-3 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div style={{ width: "90vw", margin: "auto" }}>
            {filteredRecords.length === 0 ? (
              <div className="bg-gray-100 border border-gray-200 p-6 rounded-lg text-center my-4">
                <p className="text-gray-600">
                  {searchTerm ? `No HR Associates found matching "${searchTerm}"` : 'No records found.'}
                </p>
              </div>
            ) : (
              filteredRecords.map(([username, userData], userIndex) => {
                // Ensure userData contains an 'id' field
                if (!userData.id) {
                  console.error(`Missing 'id' for user: ${username}`);
                  return null;
                }

                const userRecords = userData.records || [];
                const totals = userRecords.reduce(
                  (acc, record) => ({
                    total_resume_forwarded: acc.total_resume_forwarded + record.total_resume_forwarded,
                    total_turned_up: acc.total_turned_up + record.total_turned_up,
                    total_selected: acc.total_selected + record.total_selected,
                    total_revenue: acc.total_revenue + record.total_revenue,
                    total_target: acc.total_target + (record.candidate_target || 0),
                    total_revenue_target: acc.total_revenue_target + (record.revenue_target || 0),
                  }),
                  {
                    total_resume_forwarded: 0,
                    total_selected: 0,
                    total_revenue: 0,
                    total_turned_up: 0,
                    total_target: 0,
                    total_revenue_target: 0,
                  }
                );

                return (
                  <div key={username} className="bg-white shadow-md rounded-lg p-4 my-3">
                    <h2 className="text-xl font-semibold text-blue-600 mb-2">{userData.name}</h2>
                    <div className="overflow-x-auto">
                      <table className="w-full border border-gray-300 text-center">
                        <thead className="bg-blue-500 text-white">
                          <tr>
                            <th className="border px-4 py-2">Month Start</th>
                            <th className="border px-4 py-2">Month End</th>
                            <th className="border px-4 py-2">Resume Forwarded</th>
                            <th className="border px-4 py-2">Turned Up</th>
                            <th className="border px-4 py-2">Selected (Achieved / Target)</th>
                            <th className="border px-4 py-2">Revenue (Achieved / Target)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userRecords.map((record, rowIndex) => {
                            const rowKey = `${userIndex}-${rowIndex}`;
                            // Use monthly-specific targets from the record
                            const target = record.candidate_target || 0;
                            const revenueTarget = record.revenue_target || 0;
                            const selectedPercentage = target > 0 ? (record.total_selected / target) * 100 : 0;
                            const revenuePercentage = revenueTarget > 0 ? (record.total_revenue / revenueTarget) * 100 : 0;

                            return (
                              <React.Fragment key={rowIndex}>
                                {/* Main Row */}
                                <tr className="hover:bg-gray-100">
                                  <td className="border px-4 py-2">{record.month_start}</td>
                                  <td className="border px-4 py-2">{record.month_end}</td>
                                  <td
                                    className="border px-4 py-2 text-blue-500 cursor-pointer"
                                    onClick={() =>
                                      toggleExpandedRow(
                                        userIndex,
                                        rowIndex,
                                        "resume_forwarded",
                                        record.month_start,
                                        record.month_end,
                                        userData.id
                                      )
                                    }
                                  >
                                    {record.total_resume_forwarded}
                                  </td>
                                  <td className="border px-4 py-2">{record.total_turned_up}</td>
                                  <td
                                    className="border px-4 py-2 text-blue-500 cursor-pointer"
                                    onClick={() =>
                                      toggleExpandedRow(
                                        userIndex,
                                        rowIndex,
                                        "selected",
                                        record.month_start,
                                        record.month_end,
                                        userData.id
                                      )
                                    }
                                  >
                                    <span className={selectedPercentage >= 100 ? "text-green-600 font-bold" : selectedPercentage >= 85 ? "text-yellow-600" : "text-red-600"}>
                                      {record.total_selected}
                                    </span>
                                    {" / "}
                                    {target}
                                    {target > 0 && (
                                      <span className="ml-2 text-sm text-gray-600">({selectedPercentage.toFixed(1)}%)</span>
                                    )}
                                  </td>
                                  <td className="border px-4 py-2">
                                    <span className={revenuePercentage >= 100 ? "text-green-600 font-bold" : revenuePercentage >= 85 ? "text-yellow-600" : "text-red-600"}>
                                      {record.total_revenue.toFixed(2)}
                                    </span>
                                    {" / "}
                                    {revenueTarget.toFixed(2)} lac
                                    {revenueTarget > 0 && (
                                      <span className="ml-2 text-sm text-gray-600">({revenuePercentage.toFixed(1)}%)</span>
                                    )}
                                  </td>
                                </tr>

                                {/* Expanded Row */}
                                {expandedKey === rowKey && (
                                  <tr className="bg-gray-50">
                                    <td colSpan="6" className="p-3">
                                      <div className="grid grid-cols-1 gap-4">
                                        <h3 className="text-lg font-semibold text-blue-600 mb-2">
                                          {expandedRowType === "resume_forwarded"
                                            ? "Resume Forwarded Candidates"
                                            : "Selected Candidates"}
                                        </h3>
                                        {loadingCandidates ? (
                                          <div className="flex justify-center items-center space-x-2 text-blue-500">
                                            <span className="animate-spin text-3xl">Loading...</span>
                                            <p className="text-xl font-medium">Fetching candidate details...</p>
                                          </div>
                                        ) : (
                                          <table className="min-w-full bg-white border rounded-lg shadow-md">
                                            <thead>
                                              <tr className="bg-blue-500 text-white text-left">
                                                <th className="py-3 px-4">Candidate Name</th>
                                                <th className="py-3 px-4">Hospital</th>
                                                <th className="py-3 px-4">City</th>
                                                <th className="py-3 px-4">State</th>
                                                <th className="py-3 px-4">Speciality</th>
                                                <th className="py-3 px-4">Status</th>
                                                <th className="py-3 px-4">Revenue</th>
                                                <th className="py-3 px-4">Date Entered</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {candidateDetails.length > 0 ? (
                                                candidateDetails.map((candidate) => (
                                                  <tr key={candidate.id}>
                                                    <td className="py-3 px-4">{candidate.candidate_name}</td>
                                                    <td className="py-3 px-4">{candidate.hospital}</td>
                                                    <td className="py-3 px-4">{candidate.city}</td>
                                                    <td className="py-3 px-4">{candidate.state}</td>
                                                    <td className="py-3 px-4">{candidate.speciality}</td>
                                                    <td className="py-3 px-4">{candidate.status}</td>
                                                    <td className="py-3 px-4">{candidate.revenue}</td>
                                                    <td className="py-3 px-4">{candidate.date_entered}</td>
                                                  </tr>
                                                ))
                                              ) : (
                                                <tr>
                                                  <td colSpan="8" className="py-8 text-center text-gray-500">
                                                    No candidate details available.
                                                  </td>
                                                </tr>
                                              )}
                                            </tbody>
                                          </table>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                          {/* Totals Row */}
                          <tr className="bg-gray-300 font-bold">
                            <td colSpan={2} className="border px-4 py-2">Total</td>
                            <td className="border px-4 py-2">{totals.total_resume_forwarded}</td>
                            <td className="border px-4 py-2">{totals.total_turned_up}</td>
                            <td className="border px-4 py-2">
                              {totals.total_selected} / {totals.total_target}
                            </td>
                            <td className="border px-4 py-2">
                              {totals.total_revenue.toFixed(2)} / {totals.total_revenue_target.toFixed(2)} lac
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterMonthlyRecords;